import React, { useState, useEffect } from 'react';
import { 
  BuildingStorefrontIcon, 
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  UsersIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import AddMerchantModal from './AddMerchantModal';

interface Merchant {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  business_name?: string;
  business_address?: string;
  approval_status: string;
  is_suspended: boolean;
  created_at: string;
  wallet_balance: number;
  stats: {
    total_orders: number;
    completed_orders: number;
    total_revenue: number;
    products: number;
    invoices: number;
  };
}

interface MerchantAnalytics {
  total_merchants: number;
  active_merchants: number;
  pending_merchants: number;
  total_merchant_revenue: number;
  total_merchant_orders: number;
}

const AdminMerchantManagement: React.FC = () => {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [analytics, setAnalytics] = useState<MerchantAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'approved' | 'pending' | 'suspended'>('all');
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showAddMerchantModal, setShowAddMerchantModal] = useState(false);

  useEffect(() => {
    fetchMerchants();
    fetchAnalytics();
  }, [filter]);

  const fetchMerchants = async () => {
    try {
      const statusFilter = filter === 'all' ? null : filter === 'suspended' ? null : filter;
      const response = await api.get('/admin/merchants', {
        params: statusFilter ? { status: statusFilter } : {}
      });
      
      let merchantsList = response.data.merchants || [];
      
      if (filter === 'suspended') {
        merchantsList = merchantsList.filter((m: Merchant) => m.is_suspended);
      }
      
      setMerchants(merchantsList);
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/admin/merchants/analytics/overview');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleApproveMerchant = async (merchantId: number) => {
    if (!window.confirm('Are you sure you want to approve this merchant?')) return;
    
    try {
      await api.post(`/admin/merchants/${merchantId}/approve`);
      alert('Merchant approved successfully');
      fetchMerchants();
      fetchAnalytics();
    } catch (error) {
      console.error('Error approving merchant:', error);
      alert('Failed to approve merchant');
    }
  };

  const handleSuspendMerchant = async () => {
    if (!selectedMerchant || !suspendReason.trim()) {
      alert('Please provide a suspension reason');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('reason', suspendReason);
      
      await api.post(`/admin/merchants/${selectedMerchant.id}/suspend`, formData);
      alert('Merchant suspended successfully');
      setShowSuspendModal(false);
      setSuspendReason('');
      setSelectedMerchant(null);
      fetchMerchants();
      fetchAnalytics();
    } catch (error) {
      console.error('Error suspending merchant:', error);
      alert('Failed to suspend merchant');
    }
  };

  const getStatusBadge = (merchant: Merchant) => {
    if (merchant.is_suspended) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Suspended</span>;
    }
    if (merchant.approval_status === 'approved') {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
    }
    if (merchant.approval_status === 'pending') {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{merchant.approval_status}</span>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Merchant Management</h1>
          <p className="mt-2 text-gray-600">Oversee and manage all merchant accounts</p>
        </div>
        <button
          onClick={() => setShowAddMerchantModal(true)}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-volexx font-semibold hover:bg-purple-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Merchant
        </button>
      </div>

      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-sky-100 rounded-lg">
                <BuildingStorefrontIcon className="h-6 w-6 text-sky-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Merchants</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_merchants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.active_merchants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <UsersIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.pending_merchants}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <CurrencyDollarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">₦{analytics.total_merchant_revenue.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 bg-orange-100 rounded-lg">
                <ShoppingBagIcon className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Orders</p>
                <p className="text-2xl font-bold text-gray-900">{analytics.total_merchant_orders}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">All Merchants</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'all' ? 'bg-sky-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('approved')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'approved' ? 'bg-sky-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'pending' ? 'bg-sky-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter('suspended')}
                className={`px-4 py-2 rounded-lg ${
                  filter === 'suspended' ? 'bg-sky-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Suspended
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Merchant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stats</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {merchants.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                    No merchants found
                  </td>
                </tr>
              ) : (
                merchants.map((merchant) => (
                  <tr key={merchant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{merchant.full_name}</div>
                        <div className="text-sm text-gray-500">{merchant.email}</div>
                        <div className="text-sm text-gray-500">{merchant.phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{merchant.business_name || 'N/A'}</div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">{merchant.business_address || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{merchant.stats.total_orders} orders</div>
                      <div className="text-sm text-gray-500">{merchant.stats.products} products</div>
                      <div className="text-sm text-gray-500">{merchant.stats.invoices} invoices</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">₦{merchant.stats.total_revenue.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">Wallet: ₦{merchant.wallet_balance.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(merchant)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {merchant.approval_status === 'pending' && (
                          <button
                            onClick={() => handleApproveMerchant(merchant.id)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Approve
                          </button>
                        )}
                        {!merchant.is_suspended && merchant.approval_status === 'approved' && (
                          <button
                            onClick={() => {
                              setSelectedMerchant(merchant);
                              setShowSuspendModal(true);
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Suspend
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showSuspendModal && selectedMerchant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Suspend Merchant
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to suspend <strong>{selectedMerchant.full_name}</strong>.
              Please provide a reason:
            </p>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              rows={4}
              placeholder="Enter suspension reason..."
            />
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSuspendReason('');
                  setSelectedMerchant(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendMerchant}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Suspend Merchant
              </button>
            </div>
          </div>
        </div>
      )}

      <AddMerchantModal
        isOpen={showAddMerchantModal}
        onClose={() => setShowAddMerchantModal(false)}
        onSuccess={() => {
          fetchMerchants();
          fetchAnalytics();
        }}
      />
    </div>
  );
};

export default AdminMerchantManagement;
